// SecurityTab — 2FA, login sessions, API keys
import { Key, Monitor, Shield, ShieldCheck, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL, apiFetch } from '../../config/api'

interface Session {
  id: string
  device: string
  ip: string
  last_active: string
  current: boolean
}

export default function SecurityTab({ userId }: { userId: number }) {
  const [twoFAEnabled, setTwoFAEnabled] = useState(false)
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    apiFetch(`${API_URL}/api/users/me/sessions`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        if (d?.sessions) setSessions(d.sessions)
        if (d?.two_fa_enabled !== undefined) setTwoFAEnabled(d.two_fa_enabled)
        setLoading(false)
      })
      .catch(() => {
        // Endpoint henuz yoksa default goster
        setSessions([{ id: 'current', device: 'This browser', ip: '—', last_active: new Date().toISOString(), current: true }])
        setLoading(false)
      })
  }, [userId])

  const toggle2FA = async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/users/me/2fa/${twoFAEnabled ? 'disable' : 'enable'}`, { method: 'POST' })
      if (r.ok) setTwoFAEnabled(!twoFAEnabled)
    } catch { /* silent */ }
  }

  const revokeSession = async (id: string) => {
    try {
      await apiFetch(`${API_URL}/api/users/me/sessions/${id}`, { method: 'DELETE' })
      setSessions(prev => prev.filter(s => s.id !== id))
    } catch { /* silent */ }
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>

  const cardStyle: React.CSSProperties = { padding: 16, borderRadius: 10, background: 'var(--body-bg)', border: '1px solid var(--border-color)' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 2FA */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {twoFAEnabled ? <ShieldCheck size={18} color="#10b981" /> : <Shield size={18} color="var(--text-secondary)" />}
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Two-Factor Authentication</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{twoFAEnabled ? 'Enabled — your account is protected' : 'Add extra security to your account'}</div>
            </div>
          </div>
          <button onClick={() => { void toggle2FA() }} style={{ padding: '7px 14px', borderRadius: 8, background: twoFAEnabled ? 'rgba(239,68,68,0.1)' : '#667eea', border: twoFAEnabled ? '1px solid rgba(239,68,68,0.3)' : 'none', color: twoFAEnabled ? '#ef4444' : '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {twoFAEnabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>

      {/* Sessions */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Monitor size={15} color="#6366f1" />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Active Sessions</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sessions.map(s => (
            <div key={s.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {s.device}
                  {s.current && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: 700 }}>Current</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.ip} · {new Date(s.last_active).toLocaleDateString()}</div>
              </div>
              {!s.current && (
                <button onClick={() => { void revokeSession(s.id) }} style={{ padding: '5px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.1)', border: 'none', color: '#ef4444', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Trash2 size={12} /> Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* API Keys */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Key size={15} color="#f59e0b" />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>API Keys</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>Manage your API keys for programmatic access</div>
        <button style={{ padding: '7px 14px', borderRadius: 8, background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Generate New Key
        </button>
      </div>
    </div>
  )
}
